import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { type FC, Suspense, useRef } from 'react';
import * as THREE from 'three';
import { Vector2, Vector3 } from 'three';
import { CALLBACK_ORDER_TEXT_LABEL } from './FrameCallbackOrder';

export interface TextLabelProps {
  position: Parameters<THREE.Vector3['set']>;
  text: string;
  color: string;
  fontSize: number;
  font?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  strokeColor?: number;
  strokeOpacity?: number;
}

export const TextLabel: FC<TextLabelProps> = ({
  position,
  text,
  color,
  fontSize,
  font,
  fillOpacity,
  strokeWidth,
  strokeColor,
  strokeOpacity,
}) => {
  const group = useRef<THREE.Group | null>(null);

  const self = useRef<{ position: Vector3; cameraPosition: Vector3; screenSize: Vector2 }>({
    position: new Vector3(...position),
    cameraPosition: new Vector3(0, 0, 0),
    screenSize: new Vector2(),
  });

  useFrame(({ camera, gl }) => {
    let scale = 1;
    if (group.current) {
      if (camera instanceof THREE.OrthographicCamera) {
        scale = 1 / camera.zoom;
      } else if (camera instanceof THREE.PerspectiveCamera) {
        camera.getWorldPosition(self.current.cameraPosition);
        group.current.getWorldPosition(self.current.position);
        const distance = self.current.cameraPosition.distanceTo(self.current.position);
        gl.getSize(self.current.screenSize);
        const screenHalfHeight = 0.5 * self.current.screenSize.height;
        const halfFov = (camera.fov * Math.PI) / 360;
        // slope == height / distance
        const slope = Math.tan(halfFov);
        const screenDistribution = screenHalfHeight / slope;
        scale = distance / screenDistribution;
      }
      group.current.scale.set(scale, scale, scale);

      group.current.quaternion.copy(camera.quaternion);
    }
  }, CALLBACK_ORDER_TEXT_LABEL);

  return (
    <group ref={group} position={position}>
      <Suspense fallback={null}>
        <Text
          anchorX="center"
          anchorY="bottom"
          // font={'https://fonts.gstatic.com/s/raleway/v14/1Ptrg8zYS_SKggPNwK4vaqI.woff'}
          font={font}
          fontSize={fontSize}
          color={color}
          fillOpacity={fillOpacity ?? 1}
          strokeWidth={strokeWidth}
          strokeColor={strokeColor}
          strokeOpacity={strokeOpacity}
        >
          {text}
        </Text>
      </Suspense>
    </group>
  );
};
